import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import convertError from '../utils/error-converter.js';
import UserFriendlyError from '../utils/UserFriendlyError.js';
import callApi from '../utils/call-api.js';
import analyzeApiResponse from '../utils/analyze-api-response.js';

export const parseUpdateNewScResponse = (data) => {
    const warnings = data?.notificationWarnings || [];

    const sc =
        data?.sc ??
        data?.newSc ??
        (() => {
            const normalized = { ...data };
            delete normalized.notificationWarnings;
            return normalized;
        })();

    return { sc, notificationWarnings: warnings };
};

/**
 * Custom React hook for managing New SC(Success Consultant)-related data.
 *
 * @returns {Object} An object containing state variables and functions related to user data.
 * @property {Array<Object>} newScs - The array of returned new SCs (empty if none returned).
 * @property {number} totalPages - The total number of pages for pagination.
 * @property {Object|null} newSc - The returned new SC (null if none returned).
 * @property {Object|null} addToPlatformsResult - The result object of the process with http status codes.
 * @property {boolean} loading - A boolean indicating whether data is currently being loaded.
 * @property {Object|null} error - An error object containing details about any encountered error.
 * @property {Function} searchNewScs - A function to search for new SCs based on criteria.
 * @property {Function} createNewSc - A function to create a new SC.
 * @property {Function} updateNewSc - A function to update an new SC.
 * @property {Function} getNewSc - A function to get a specific new SC by id.
 * @property {Function} addUserToPlatforms - A function to add a user by id to various software platforms and send automated emails
 */
const useNewScs = () => {
    const [newScs, setNewScs] = useState([]);
    const [logs, setLogs] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [newSc, setNewSc] = useState(null);
    const [addToPlatformsResult, setAddToPlatformsResult] = useState(null);
    const [createNewScResult, setCreateNewScResult] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notificationWarning, setNotificationWarning] = useState(null);

    // Checkpoint-specific state
    const [checkpointResults, setCheckpointResults] = useState(null);
    const [loadingCheckpoint, setLoadingCheckpoint] = useState(false);
    const [checkpointError, setCheckpointError] = useState(null);

    // Filters state (includes pagination)
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        action: '',
        scName: '',
        startDate: '',
        endDate: ''
    });
    // flag to control first fetch
    const [shouldFetchLogs, setShouldFetchLogs] = useState(false);

    // Debounced search handling
    const [searchName, setSearchName] = useState('');

    /**
     * Searches for new SCs based on specified criteria.
     *
     * @async
     * @function
     * @param {string} [searchString=''] - The search string for filtering new SCs by name.
     * @param {string} [type='active'] - The status type of new SCs to search (e.g., 'active', 'retired', 'expired', 'awaitingAssignment', 'all').
     * @param {string} [software=null] - the software to filter by
     * @param {number} [currentPage=1] - The current page for pagination.
     * @param {number} [limit=40] - The number of offices to retrieve per page.
     * @param {string} [sortBy='lastName:asc'] - The key to sort by
     * @param {boolean} [paginate=true] - whether to paginate the results
     * @returns {Promise<Array>} A promise that resolves with a list of returned users.
     */
    const searchNewScs = async (
        searchString = '',
        type = 'active',
        software = null,
        currentPage = 1,
        limit = 40,
        sortBy = 'lastName:asc',
        paginate = true
    ) => {
        try {
            setLoading(true);
            setError(null);

            const path = software
                ? `/new-SCs/search?search=${searchString}&type=${type}&software=${software}&limit=${limit}&page=${currentPage}&sortBy=${sortBy}&paginate=${paginate}`
                : `/new-SCs/search?search=${searchString}&type=${type}&limit=${limit}&page=${currentPage}&sortBy=${sortBy}&paginate=${paginate}`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const { results: returnedNewScs, totalPages: returnedTotalPages } =
                await response.json();
            setNewScs(returnedNewScs);
            setTotalPages(returnedTotalPages);
            return returnedNewScs;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Creates a new SC.
     *
     * @async
     * @function
     * @param {Object} newScFields - The fields for creating the new SC, passed from formik. Reference config/form-fields.js or validation/create-new-sc-validations.js for details on properties.
     * @returns {Promise<Object>} A promise that resolves when the new SC creation is complete with the response object.
     */
    const createNewSc = async (newScFields) => {
        try {
            setLoading(true);
            setError(null);

            const path = `/new-SCs`;
            const response = await callApi(path, 'post', newScFields);

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const processResult = await response.json();
            setCreateNewScResult(processResult);
            return processResult;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Updates a new SC.
     *
     * @async
     * @function
     * @param {String} newScId
     * @param {Object} updateNewScFields - The fields for updating the new SC, passed from formik. Reference config/form-fields.js or validation/create-new-sc-validations.js for details on properties.
     * @returns {Promise<NewSc>} A promise that resolves when the new SC update is complete with the new SC object.
     */
    const updateNewSc = async (newScId, updateNewScFields) => {
        try {
            setLoading(true);
            setError(null);
            setNotificationWarning(null);

            const updateNewScFieldsModified = { ...updateNewScFields };

            // Remove any more fields that are not allowed
            delete updateNewScFieldsModified.id;
            if (updateNewScFieldsModified.software) {
                updateNewScFieldsModified.software =
                    updateNewScFieldsModified.software.map((obj) => {
                        // Destructure and remove _id and leave the rest
                        const { _id, ...rest } = obj;
                        return rest;
                    });
            }
            const path = `/new-SCs/${newScId}`;
            const response = await callApi(
                path,
                'patch',
                updateNewScFieldsModified
            );

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const responseData = await response.json();
            const { sc, notificationWarnings } =
                parseUpdateNewScResponse(responseData);

            if (notificationWarnings.length > 0) {
                const warningText = notificationWarnings.join('\n');
                setNotificationWarning(warningText);
                toast(warningText, {
                    duration: 8000,
                    id: `new-sc-notification-warning-${newScId}`,
                    icon: '⚠️'
                });
            }

            setNewSc(sc);
            return { sc, notificationWarnings };
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            toast.error(convertedUserFriendlyError.message, {
                duration: 8000,
                id: `new-sc-update-error-${newScId}`
            });
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Gets a single new SC and it's info by ID.
     *
     * @async
     * @function
     * @param {string} newScId
     * @returns {Promise<NewSc>} A promise that resolves with the returned new SC.
     */
    const getNewSc = useCallback(async (newScId) => {
        try {
            setLoading(true);
            setError(null);

            const path = `/new-SCs/${newScId}`;
            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const returnedNewSc = await response.json();
            setNewSc(returnedNewSc);
            return returnedNewSc;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Triggers the automation process for adding a user to various software platforms and sending setup emails.
     *
     * @async
     * @function
     * @param {Object} userData - The fields for triggering the automation, passed from formik. Reference config/form-fields.js or validation/add-user-to-platforms-validations.js for details on properties.
     * @returns {Promise<Object>} A promise that resolves when the process is complete with the result object.
     */
    const addUserToPlatforms = async (userData) => {
        try {
            setLoading(true);
            setError(null);

            const path = `/new-SCs/add-to-platforms`;
            const response = await callApi(path, 'post', userData);

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const processResult = await response.json();
            setAddToPlatformsResult(processResult);
            return processResult;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reactivates a New SC by ID.
     *
     * @async
     * @function
     * @param {string} newScId
     * @returns {Promise<Object>} The updated SC object.
     */
    const reactivateNewSc = async (newScId) => {
        try {
            setLoading(true);
            setError(null);

            const path = `/new-SCs/${newScId}/reactivate`;
            const response = await callApi(path, 'post');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const { sc } = await response.json();
            setNewSc(sc); // store in state for detail pages
            return sc;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // New function: fetch SC checkpoint results
    const getScCheckpointResults = useCallback(async (scIdentifier) => {
        try {
            setLoadingCheckpoint(true);
            setCheckpointError(null);

            const path = `/new-SCs/checkpoints?scIdentifier=${encodeURIComponent(
                scIdentifier
            )}`;
            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const data = await response.json();
            setCheckpointResults(data.results);
            return data.results;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setCheckpointError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoadingCheckpoint(false);
        }
    }, []);

    /**
     * Fetches SC activity logs with optional filters and pagination.
     *
     * @async
     * @function
     * @param {Object} params - Query parameters for the request.
     * @param {number} [params.page=1] - Page number.
     * @param {number} [params.limit=10] - Number of records per page.
     * @param {string} [params.action] - Filter by action type.
     * @param {string} [params.scName] - Filter by SC name.
     * @param {string} [params.startDate] - Start date (YYYY-MM-DD).
     * @param {string} [params.endDate] - End date (YYYY-MM-DD).
     * @returns {Promise<Object|null>} The activity log data or null on error.
     */
    const getScActivityLogs = async () => {
        try {
            setLoading(true);
            setError(null);

            const queryParams = new URLSearchParams({
                page: filters.page,
                limit: filters.limit,
                ...(filters.action ? { action: filters.action } : {}),
                ...(filters.scName ? { scName: filters.scName } : {}),
                ...(filters.startDate ? { startDate: filters.startDate } : {}),
                ...(filters.endDate ? { endDate: filters.endDate } : {})
            });

            const path = `/new-SCs/sc-activity-logs?${queryParams.toString()}`;
            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const data = await response.json();

            setLogs(data.logs || []);
            setTotalPages(data.totalPages || 0);
            setTotalRecords(data.totalRecords || 0);
            // console.log('📥 Frontend Received Logs:', data);
            return data;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch logs whenever filters change
    useEffect(() => {
        if (shouldFetchLogs) {
            getScActivityLogs();
        }
    }, [filters, shouldFetchLogs]);

    // Debounce searchName into filters.scName
    useEffect(() => {
        const handler = setTimeout(() => {
            setFilters((prev) => ({ ...prev, scName: searchName, page: 1 }));
        }, 500); // 500ms delay

        return () => clearTimeout(handler);
    }, [searchName]);

    return {
        newScs,
        logs,
        totalPages,
        totalRecords,
        filters,
        setFilters,
        searchName,
        setSearchName,
        newSc,
        loading,
        error,
        notificationWarning,
        searchNewScs,
        createNewSc,
        updateNewSc,
        getNewSc,
        addUserToPlatforms,
        addToPlatformsResult,
        createNewScResult,
        reactivateNewSc,
        triggerFetchLogs: () => setShouldFetchLogs(true),
        checkpointResults,
        loadingCheckpoint,
        checkpointError,
        getScCheckpointResults
    };
};

export default useNewScs;
